import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import lessons from "@/data/lessons.json";
import { DOMAINS, LEVELS, LEVEL_ORDER } from "@/data/site";
import Reveal from "@/components/Reveal";

const LessonCard = ({ lesson, level }) => {
  const domain = DOMAINS[lesson.domain] || DOMAINS.education;
  return (
    <Link
      to={`/lessons/${lesson.id}`}
      data-testid={`lesson-card-${lesson.id}`}
      className="group flex h-full flex-col rounded-2xl border border-white/8 bg-navy-800/80 p-5 transition duration-300 hover:-translate-y-1 hover:border-teal-500/40 sm:p-6"
    >
      <div className="mb-4 flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-500/12 text-teal-400">
          <domain.icon className="h-5 w-5" />
        </span>
        <div className="flex items-center gap-2">
          {lesson.isExtra && (
            <span className="rounded-full bg-gold/15 px-2.5 py-1 text-[11px] font-bold text-gold">درس إضافي</span>
          )}
          <span
            className="rounded-full px-2.5 py-1 font-en text-[11px] font-bold"
            style={{ color: level.color, backgroundColor: `${level.color}1f` }}
          >
            {level.key}
          </span>
        </div>
      </div>
      <h3 dir="ltr" className="text-left font-en text-lg font-bold leading-snug text-ink">
        {lesson.title}
      </h3>
      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-ink-soft">{lesson.subtitle}</p>
      <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4">
        <span className="text-xs text-ink-mute">{domain.ar}</span>
        <span className="flex items-center gap-1.5 text-sm font-bold text-teal-400 transition group-hover:gap-2.5">
          اقرأ الدرس
          <ArrowLeft className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

const Library = () => (
  <section id="library" className="bg-navy-900 py-16 sm:py-24">
    <div className="container-x">
      <Reveal>
        <p className="mb-3 text-sm font-semibold text-teal-400">مكتبة الدروس</p>
        <h2 className="max-w-xl text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl">
          {lessons.length} درساً مقالياً بثلاثة مستويات، مع مفردات ومحاكاة كاملة لاختبار المحادثة
        </h2>
      </Reveal>

      {LEVEL_ORDER.map((key, li) => {
        const level = LEVELS[key];
        const items = lessons.filter((l) => l.level === key);
        return (
          <div key={key} className="mt-12" data-testid={`library-section-${key}`}>
            <Reveal>
              <div className="mb-5 flex items-center gap-3">
                <span
                  className="flex h-11 min-w-[52px] items-center justify-center rounded-xl px-2 font-en text-base font-extrabold"
                  style={{ color: level.color, backgroundColor: `${level.color}1f` }}
                >
                  {key}
                </span>
                <div>
                  <h3 className="text-lg font-bold sm:text-xl">مستوى {key} — {level.name}</h3>
                  <p className="text-xs text-ink-mute">{items.length} مقالات بأمثلة واختبارات محادثة</p>
                </div>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3">
              {items.map((lesson, i) => (
                <Reveal key={lesson.id} delay={Math.min(i, 2) * 0.06} className="h-full">
                  <LessonCard lesson={lesson} level={level} />
                </Reveal>
              ))}
            </div>
          </div>
        );
      })}

      <Reveal>
        <p className="mt-12 flex items-center gap-2 text-sm text-ink-mute">
          <BookOpen className="h-4 w-4" />
          كل درس يحتوي: نصاً للقراءة، قائمة مفردات كاملة مع الترجمة، واختبار محاكاة IELTS Speaking بأجوبته النموذجية.
        </p>
      </Reveal>
    </div>
  </section>
);

export default Library;
